// Chat channel type helpers. Channel types come from the API as strings: Team, League, Direct, Spares, LeagueAdmins.

export const isLeagueChannel = (channel) => channel?.channelType === 'League';
export const isDirectChannel = (channel) => channel?.channelType === 'Direct';
export const isSparesChannel = (channel) => channel?.channelType === 'Spares';
export const isLeagueAdminsChannel = (channel) => channel?.channelType === 'LeagueAdmins';

const LEAGUE_WIDE = {
  League: { label: 'League', caption: 'League chat', audience: 'league' },
  LeagueAdmins: { label: 'League admins', caption: 'League admins chat', audience: 'fellow league admins' },
  Spares: { label: 'Spares', caption: 'Spares chat', audience: 'league\'s spares' },
};

/** One chat per league: the league chat plus its league admins and spares chats. */
export const isLeagueWideChannel = (channel) => Boolean(LEAGUE_WIDE[channel?.channelType]);

/** "League chat", "Spares chat" or "League admins chat"; null for team and direct chats. */
export const leagueWideCaption = (channel) => LEAGUE_WIDE[channel?.channelType]?.caption ?? null;

/** Short label used before the league name in the chat list ("Spares · Thursday League"). */
export const leagueWideLabel = (channel) => LEAGUE_WIDE[channel?.channelType]?.label ?? null;

/** Who the empty-state greeting addresses ("Say hello to your …"). */
export const channelAudience = (channel) => LEAGUE_WIDE[channel?.channelType]?.audience ?? 'team';

const TYPE_LABELS = {
  League: 'League', LeagueAdmins: 'League Admins', Spares: 'Spares', Team: 'Team', Direct: 'Direct',
};

/** Chip/filter label for a channel type. */
export const channelTypeLabel = (channelType) => TYPE_LABELS[channelType] || 'Team';

/** Channel types admins can filter by (direct chats are private and never listed). */
export const ADMIN_CHANNEL_TYPES = ['League', 'LeagueAdmins', 'Spares', 'Team'];

const TYPE_ORDER = { League: 0, LeagueAdmins: 1, Spares: 2 };

/** League-wide chats first (league, league admins, spares), then team and direct chats. Matches the server. */
export const channelTypeOrder = (channel) => TYPE_ORDER[channel?.channelType] ?? 3;

/** The name a channel shows when no custom name is set (mirrors the server's ChatChannelNames.Display). */
export const defaultChannelName = (channel) => {
  if (!channel) return '';
  if (channel.channelType === 'Team') return channel.teamName || '';
  const league = channel.leagueName || '';
  if (isSparesChannel(channel)) return `${league} Spares`.trim();
  if (isLeagueAdminsChannel(channel)) return `${league} Admins`.trim();
  return league;
};

/** The current user's spares chat for a league, from the channel list in ChatContext. */
export const findSparesChannel = (channels, leagueId) => (channels || [])
  .find((c) => isSparesChannel(c) && leagueId != null && Number(c.leagueId) === Number(leagueId)) || null;
